/**
 * Utilitários para garantir que o CSS funcione corretamente em produção quando implantado no S3
 * 
 * Este arquivo contém funções para preservar classes CSS que podem ser removidas
 * pelo processo de build em produção. Ele também fornece uma maneira consistente
 * de aplicar classes tanto no ambiente de desenvolvimento (com Tailwind CDN)
 * quanto em produção (com CSS otimizado).
 */

/**
 * Garante que as classes do CSS sejam preservadas durante a minificação do CSS
 * no processo de build para produção.
 * 
 * @param classes String com as classes CSS ou array de classes CSS
 * @returns Uma string concatenada com todas as classes CSS
 */
export const preserveClasses = (classes: string | string[]): string => {
  if (Array.isArray(classes)) {
    return classes.filter(Boolean).join(' ');
  }
  return classes;
};

/**
 * Verifica se o ambiente atual é produção ou desenvolvimento
 * Útil para aplicar estilos condicionalmente
 */
export const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production';
};

/**
 * Classes CSS para a paginação que devem ser preservadas durante o processo de build
 */
export const paginationClasses = {
  container: preserveClasses([
    'flex', 
    'flex-col', 
    'sm:flex-row', 
    'items-center', 
    'justify-center', 
    'gap-4',
    'mt-6',
    'w-full'
  ]),
  info: preserveClasses(['text-sm', 'text-gray-500', 'text-center']),
  controls: preserveClasses(['flex', 'items-center', 'justify-center', 'space-x-2', 'w-full']),
  pageNumbers: preserveClasses(['flex', 'items-center', 'justify-center', 'space-x-1']),
  ellipsis: preserveClasses(['px-3', 'py-1']),
  pageButton: (isActive: boolean): string => preserveClasses([
    'px-3', 
    'py-1', 
    'rounded',
    isActive ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
  ])
};

/**
 * Classes CSS para os botões que devem ser preservadas durante o processo de build
 */
export const buttonClasses = {
  base: 'btn',
  variant: (variant: string): string => `btn-${variant}`,
  size: (size: string): string => `btn-${size}`,
  fullWidth: 'w-full',
  disabled: preserveClasses(['opacity-50', 'cursor-not-allowed'])
};

/**
 * Classes CSS para os cards que devem ser preservadas durante o processo de build
 */
export const cardClasses = {
  container: preserveClasses([
    'bg-white',
    'border',
    'border-gray-200',
    'rounded-lg',
    'shadow',
    'overflow-hidden'
  ]),
  header: preserveClasses([
    'p-4',
    'border-b',
    'border-gray-200',
    'bg-gray-50'
  ]),
  body: preserveClasses(['p-4']),
  footer: preserveClasses([
    'p-4',
    'border-t',
    'border-gray-200',
    'bg-gray-50'
  ])
};

/**
 * Classes CSS para as tabelas que devem ser preservadas durante o processo de build
 */
export const tableClasses = {
  container: preserveClasses([
    'w-full',
    'max-w-full',
    'overflow-x-auto',
    'border',
    'border-gray-200',
    'rounded-lg',
    'table-responsive-container',
    'overflow-hidden'  // Adicionar overflow-hidden para garantir que o container controle o scroll
  ]),
  table: preserveClasses([
    'min-w-full',
    'w-full',
    'table-layout-fixed',  // Mudar para fixed para melhor controle de largura
    'divide-y',
    'divide-gray-200',
    'table-responsive'
  ]),
  thead: preserveClasses(['bg-gray-50']),
  th: preserveClasses([
    'px-4',
    'py-3',
    'text-left',
    'text-xs',
    'font-medium',
    'text-gray-500',
    'uppercase',
    'tracking-wider',
    'overflow-hidden',
    'text-overflow-ellipsis'
  ]),
  tbody: preserveClasses(['bg-white', 'divide-y', 'divide-gray-200']),
  tr: preserveClasses(['hover:bg-gray-50']),
  td: preserveClasses([
    'px-4',
    'py-3',
    'text-sm',
    'text-gray-500',
    'overflow-hidden',
    'text-overflow-ellipsis',
    'whitespace-nowrap',  // Impede quebra de linha
    'truncate'           // Garante que o texto seja truncado com "..."
  ]),
  // Colunas específicas
  colId: preserveClasses(['col-id']),
  colName: preserveClasses(['col-name', 'max-w-[200px]']),
  colStatus: preserveClasses(['col-status']),
  colDate: preserveClasses(['col-date']),
  colActions: preserveClasses(['col-actions', 'whitespace-nowrap'])
};

/**
 * Classes CSS para os editores de código (Tool pages) que devem ser preservadas
 */
export const toolEditorClasses = {
  // Layout containers
  mainContainer: preserveClasses(['min-h-screen', 'bg-gray-50']),
  headerContainer: preserveClasses(['bg-white', 'border-b', 'border-gray-200']),
  headerContent: preserveClasses(['px-6', 'py-4']),
  formContainer: preserveClasses(['flex', 'h-[calc(100vh-140px)]']),
  
  // Left panel (form)
  leftPanel: preserveClasses(['w-[400px]', 'bg-white', 'border-r', 'border-gray-200', 'overflow-y-auto']),
  leftPanelContent: preserveClasses(['p-6', 'space-y-6']),
  sectionHeader: preserveClasses(['flex', 'items-center', 'justify-between', 'pb-4', 'border-b', 'border-gray-200']),
  sectionTitle: preserveClasses(['text-xl', 'font-semibold', 'text-gray-900', 'flex', 'items-center']),
  unsavedIndicator: preserveClasses(['text-xs', 'text-amber-600', 'bg-amber-50', 'px-2', 'py-1', 'rounded-full']),
  
  // Status badges
  statusBadge: {
    active: preserveClasses(['text-green-600', 'bg-green-50', 'border-green-200']),
    inactive: preserveClasses(['text-gray-600', 'bg-gray-50', 'border-gray-200']),
    deprecated: preserveClasses(['text-red-600', 'bg-red-50', 'border-red-200']),
    base: preserveClasses(['mt-2', 'inline-flex', 'items-center', 'px-2.5', 'py-1', 'rounded-full', 'text-xs', 'font-medium', 'border'])
  },
  
  // Info boxes
  infoBox: preserveClasses(['bg-blue-50', 'border', 'border-blue-200', 'rounded-lg', 'p-4']),
  errorBox: preserveClasses(['bg-red-50', 'border', 'border-red-200', 'text-red-700', 'p-4', 'rounded-lg', 'flex', 'items-start']),
  successBox: preserveClasses(['bg-green-50', 'border', 'border-green-200', 'text-green-700', 'p-4', 'rounded-lg', 'flex', 'items-start']),
  guidelinesBox: preserveClasses(['bg-gray-50', 'border', 'border-gray-200', 'rounded-lg', 'p-4', 'space-y-3']),
  metadataBox: preserveClasses(['bg-gray-50', 'border', 'border-gray-200', 'rounded-lg', 'p-4', 'space-y-2']),
  
  // Right panel (editor)
  rightPanel: preserveClasses(['flex-1', 'bg-gray-900', 'relative']),
  editorHeader: preserveClasses(['absolute', 'top-0', 'left-0', 'right-0', 'bg-gray-800', 'border-b', 'border-gray-700', 'px-4', 'py-3', 'z-10']),
  editorContent: preserveClasses(['pt-14', 'h-full']),
  editorStatusBar: preserveClasses(['absolute', 'bottom-0', 'left-0', 'right-0', 'bg-gray-800', 'border-t', 'border-gray-700', 'px-4', 'py-2']),
  
  // Loading states
  loadingContainer: preserveClasses(['min-h-screen', 'flex', 'items-center', 'justify-center']),
  loadingSpinner: preserveClasses(['animate-spin', 'rounded-full', 'h-12', 'w-12', 'border-b-2', 'border-blue-500', 'mx-auto']),
  
  // Buttons
  fullWidthButton: preserveClasses(['w-full', 'justify-center']),
  buttonSpinner: preserveClasses(['animate-spin', 'rounded-full', 'h-4', 'w-4', 'border-b-2', 'border-white', 'mr-2'])
};